import React, { useState, useEffect, useMemo } from "react";
import { Box, Text, VStack, Button, Portal, usePrefersReducedMotion, HStack, Flex } from "@chakra-ui/react";
import { useWindowSize } from "react-use";
import Confetti from "react-confetti";
import { AnimatePresence, motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionText = motion(Text);

const now = new Date();
const targetDate = new Date(
  2026,
  0,
  1,
  0,
  0,
  0
);

const backgroundImageUrl = "/2026theme.jpeg";

const CountdownToEndOf2026 = ({ isOpen = true, onClose }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isNewYear: false, // True once 2026 begins
  });
  const [confettiPieces, setConfettiPieces] = useState(0);
  const [confettiRecycle, setConfettiRecycle] = useState(false);
  const [showCelebrationImage, setShowCelebrationImage] = useState(false);

  const { width, height } = useWindowSize();
  const prefersReducedMotion = usePrefersReducedMotion();

  const units = useMemo(
    () => [
      { label: "Days", value: timeLeft.days.toString() },
      { label: "Hours", value: timeLeft.hours.toString().padStart(2, "0") },
      { label: "Minutes", value: timeLeft.minutes.toString().padStart(2, "0") },
      { label: "Seconds", value: timeLeft.seconds.toString().padStart(2, "0"), accent: true },
    ],
    [timeLeft.days, timeLeft.hours, timeLeft.minutes, timeLeft.seconds]
  );

  useEffect(() => {
    const updateCountdown = () => {
      const diff = targetDate - new Date();

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);
        setTimeLeft({ days, hours, minutes, seconds, isNewYear: false });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isNewYear: true });
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 250);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!timeLeft.isNewYear) {
      setConfettiPieces(0);
      setConfettiRecycle(false);
      setShowCelebrationImage(false);
      return;
    }

    const durationMs = 60 * 1000;
    const startTime = Date.now();
    setConfettiRecycle(true);

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remainingRatio = Math.max(0, 1 - elapsed / durationMs);
      const pieces = Math.max(0, Math.floor(1600 * remainingRatio));
      setConfettiPieces(pieces);

      if (elapsed >= durationMs) {
        setConfettiRecycle(false);
        setConfettiPieces(0);
        clearInterval(interval);
      }
    }, 250);

    return () => clearInterval(interval);
  }, [timeLeft.isNewYear]);

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <MotionBox
        position="fixed"
        inset="0"
        w="100%"
        minH="100vh"
        bgGradient="linear(to-br, #fef9c3, #dbeafe 45%, #e9d5ff)"
        border="1px solid"
        borderColor="blackAlpha.100"
        borderRadius="0px"
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        overflow="hidden"
        zIndex={9999}
        style={{ backgroundSize: "200% 200%" }}
        initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
        animate={
          prefersReducedMotion
            ? { opacity: 1, y: 0 }
            : {
                opacity: 1,
                y: 0,
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              }
        }
        transition={
          prefersReducedMotion
            ? { duration: 0.2 }
            : {
                opacity: { duration: 0.4, ease: "easeOut" },
                y: { duration: 0.4, ease: "easeOut" },
                backgroundPosition: { duration: 14, ease: "easeInOut", repeat: Infinity },
              }
        }
      >
      {timeLeft.isNewYear && (
        <>
          {showCelebrationImage && (
            <Box
              position="absolute"
              inset="0"
              backgroundImage={`url(${backgroundImageUrl})`}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
              zIndex={0}
            />
          )}
          <Confetti
            width={width}
            height={height}
            recycle={confettiRecycle}
            numberOfPieces={confettiPieces}
            gravity={0.25}
            wind={0.02}
            opacity={0.9}
          />
        </>
      )}
      {onClose && (
        <Button
          position="absolute"
          top={6}
          right={6}
          onClick={onClose}
          bg="whiteAlpha.800"
          _hover={{ bg: "whiteAlpha.900" }}
          color="gray.700"
          borderRadius="999px"
          boxShadow="md"
        >
          Close
        </Button>
      )}
      {timeLeft.isNewYear && !showCelebrationImage ? (
        <VStack spacing={4} zIndex={1} px={[4, 8]} textAlign="center">
          <Text
            fontSize={["5xl", "7xl", "8xl"]}
            fontWeight="800"
            color="gray.800"
            lineHeight="1"
          >
            Happy New Year
          </Text>
        </VStack>
      ) : !timeLeft.isNewYear ? (
        <Flex
          zIndex={1}
          px={[3, 6]}
          gap={[3, 5, 8]}
          wrap="wrap"
          justify="center"
          align="center"
        >
          {units.map((unit, index) => (
            <HStack key={unit.label} spacing={[1, 2]} alignItems="baseline">
              <AnimatePresence mode="wait" initial={false}>
                <MotionText
                  key={unit.value}
                  fontSize={unit.accent ? ["4xl", "6xl", "8xl"] : ["4xl", "6xl", "8xl"]}
                  fontWeight="800"
                  color={unit.accent ? "orange.500" : "gray.800"}
                  letterSpacing="0.05em"
                  fontVariantNumeric="tabular-nums"
                  display="inline-block"
                  minW={["2.6ch", "3ch", "3.4ch"]}
                  textAlign="center"
                  lineHeight="1"
                  initial={
                    prefersReducedMotion
                      ? false
                      : { y: 12, opacity: 0, scale: 0.98 }
                  }
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={prefersReducedMotion ? { opacity: 0 } : { y: -12, opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  {unit.value}
                </MotionText>
              </AnimatePresence>
              <Text fontSize={["xs", "sm", "md"]} color="gray.500" textTransform="uppercase" letterSpacing="0.2em">
                {unit.label}
              </Text>
              {index < units.length - 1 && (
                <Text fontSize={["3xl", "5xl", "7xl"]} color="gray.400" fontWeight="300" lineHeight="1">
                  :
                </Text>
              )}
            </HStack>
          ))}
        </Flex>
      ) : null}
      {timeLeft.isNewYear && !showCelebrationImage && (
        <Box
          position="absolute"
          inset="0"
          zIndex={2}
          onClick={() => setShowCelebrationImage(true)}
        />
      )}
      </MotionBox>
    </Portal>
  );
};

export default CountdownToEndOf2026;
